// Calculs du bracelet d'allure (page Bracelet) : temps de passage cumules
// selon une strategie de course, et decoupage en bandes imprimables.
//
// Le bracelet est une bande de BANDE.longueur x BANDE.hauteur mm : un bloc
// d'en-tete (nom, objectif), deux lignes de cases de passage, et une
// languette hachuree a coller pour le refermer autour du poignet.

export type Strategie = 'reguliere' | 'negative' | 'progressive';
export type Papier = 'blanc' | 'jaune' | 'nuit';
// Ou tombent les temps de passage : aux panneaux kilometriques de la course,
// ou aux bips de la montre (qui mesure plus que la distance officielle).
export type Reperes = 'panneaux' | 'montre';

export type ParametresBracelet = {
  nom: string;
  distanceKm: number;
  tempsSec: number;          // temps final vise
  strategie: Strategie;
  ecart: number;             // ecart d'allure debut/fin, en fraction (0.015 = 1,5 %)
  pasKm: number;             // un passage tous les pasKm
  afficherAllure: boolean;   // allure du troncon sous chaque temps
  miCourse: boolean;         // ajouter le passage a mi-distance
  papier: Papier;
  // Correction GPS : la montre mesure (1 + correctionGps) km par km officiel
  // (trajectoires, virages, imprecision GPS) ; 0.01 = +1 %. 0 = aucune.
  correctionGps: number;
  reperes: Reperes;
};

export type Passage = {
  km: number;                // distance du repere (officielle, ou lue sur la montre)
  kmOfficiel: number;        // distance officielle correspondante
  temps: number;             // temps cumule (s)
  tempsTroncon: number;      // depuis le passage precedent (s)
  allureTroncon: number;     // s/km sur le troncon, telle qu'affichee par la montre
  repere: boolean;           // tous les 5 km, mi-course, arrivee
  miCourse?: boolean;
  arrivee?: boolean;
};

export const BANDE = {
  longueur: 230,   // mm
  hauteur: 32,
  languette: 16,   // zone a coller
  entete: 30,
  marge: 1.2,
  caseMin: 14,     // largeur mini d'une case (lisibilite d'un temps h:mm:ss)
  caseMax: 28,
};

export const PAPIERS: Record<Papier, { libelle: string; fond: string; encre: string; doux: string; accent: string; encreAccent: string; trait: string }> = {
  blanc: { libelle: 'Blanc', fond: '#ffffff', encre: '#111111', doux: '#efefef', accent: '#ff5a1f', encreAccent: '#ffffff', trait: '#9a9a9a' },
  jaune: { libelle: 'Jaune fluo', fond: '#eef24a', encre: '#111111', doux: '#dfe336', accent: '#111111', encreAccent: '#eef24a', trait: '#6f7216' },
  nuit: { libelle: 'Bleu nuit', fond: '#17233b', encre: '#ffffff', doux: '#22314f', accent: '#ff6a33', encreAccent: '#17233b', trait: '#6c7a96' },
};

export const LIBELLES_STRATEGIE: Record<Strategie, string> = {
  reguliere: 'régulière',
  negative: 'négative',
  progressive: 'progressive',
};

const EPS = 1e-6;

/**
 * Allure (s/km) en fonction de la distance parcourue, telle que le temps
 * total sur la distance soit exactement le temps vise.
 * - reguliere : allure constante ;
 * - negative : 2e moitie plus rapide de `ecart` que la 1re ;
 * - progressive : allure qui diminue lineairement, ecart entre depart et arrivee.
 */
export function modeleAllure(p: Pick<ParametresBracelet, 'distanceKm' | 'tempsSec' | 'strategie' | 'ecart'>) {
  const D = p.distanceKm;
  const moyenne = p.tempsSec / D;
  if (p.strategie === 'negative') {
    const a1 = (2 * moyenne) / (2 - p.ecart);
    const a2 = a1 * (1 - p.ecart);
    return {
      tempsA: (x: number) => (x <= D / 2 ? a1 * x : (a1 * D) / 2 + a2 * (x - D / 2)),
      allureDebut: a1,
      allureFin: a2,
    };
  }
  if (p.strategie === 'progressive') {
    const a = moyenne * (1 + p.ecart / 2);
    const b = (moyenne * p.ecart) / D;
    return { tempsA: (x: number) => a * x - (b * x * x) / 2, allureDebut: a, allureFin: a - b * D };
  }
  return { tempsA: (x: number) => moyenne * x, allureDebut: moyenne, allureFin: moyenne };
}

/** Kilometres mesures par la montre pour un kilometre officiel. */
export function facteurGps(p: Pick<ParametresBracelet, 'correctionGps'>): number {
  return 1 + Math.max(0, p.correctionGps || 0);
}

/** Allure (s/km) lue sur la montre pour une allure officielle donnee. */
export function allureMontre(allureOfficielle: number, p: Pick<ParametresBracelet, 'correctionGps'>): number {
  return allureOfficielle / facteurGps(p);
}

/**
 * Temps de passage. Reperes "panneaux" : aux kilometres officiels (panneaux
 * de la course). Reperes "montre" : aux kilometres comptes par la montre,
 * qui arrivent plus tot que les panneaux quand elle mesure trop (correction
 * GPS). Dans les deux cas, l'allure du troncon est celle que la montre affiche.
 */
export function calculerPassages(p: ParametresBracelet): Passage[] {
  const D = p.distanceKm;
  const modele = modeleAllure(p);
  const f = facteurGps(p);
  const surMontre = p.reperes === 'montre' && f > 1;
  // Distance des reperes (officielle, ou telle que la montre la mesurera).
  const Dr = surMontre ? D * f : D;
  const officiel = (km: number) => (surMontre ? km / f : km);
  const kms: { km: number; miCourse?: boolean; arrivee?: boolean }[] = [];
  for (let k = p.pasKm; k < Dr - 0.05; k += p.pasKm) kms.push({ km: Math.round(k * 1000) / 1000 });
  if (p.miCourse && D >= 10) {
    const existant = kms.find((x) => Math.abs(x.km - Dr / 2) < EPS);
    if (existant) existant.miCourse = true;
    else kms.push({ km: Dr / 2, miCourse: true });
  }
  kms.push({ km: Dr, arrivee: true });
  kms.sort((a, b) => a.km - b.km);

  let kmPrec = 0;
  let tempsPrec = 0;
  return kms.map((x) => {
    const temps = modele.tempsA(officiel(x.km));
    // Distance du troncon lue sur la montre.
    const kmMontre = (x.km - kmPrec) * (surMontre ? 1 : f);
    const passage: Passage = {
      ...x,
      kmOfficiel: officiel(x.km),
      temps,
      tempsTroncon: temps - tempsPrec,
      allureTroncon: (temps - tempsPrec) / kmMontre,
      repere: !!x.arrivee || !!x.miCourse || Math.abs(x.km / 5 - Math.round(x.km / 5)) < EPS,
    };
    kmPrec = x.km;
    tempsPrec = temps;
    return passage;
  });
}

/** Repartit les passages en bandes de taille egale (2 lignes de cases par bande). */
export function decouperEnBandes(passages: Passage[]): Passage[][] {
  const zone = BANDE.longueur - BANDE.languette - BANDE.entete - BANDE.marge * 2;
  const parBande = Math.floor(zone / BANDE.caseMin) * 2;
  const nbBandes = Math.max(1, Math.ceil(passages.length / parBande));
  const taille = Math.ceil(passages.length / nbBandes);
  const bandes: Passage[][] = [];
  for (let i = 0; i < passages.length; i += taille) bandes.push(passages.slice(i, i + taille));
  return bandes;
}

/** 3:30:00, 25:04 (format chrono, compact pour le bracelet). */
export function formaterChrono(secondes: number, avecHeures = false): string {
  const total = Math.round(secondes);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const dd = (v: number) => String(v).padStart(2, '0');
  return h > 0 || avecHeures ? `${h}:${dd(m)}:${dd(s)}` : `${m}:${dd(s)}`;
}

export function formaterKm(km: number, decimales = 3): string {
  return km.toLocaleString('fr-FR', { maximumFractionDigits: decimales });
}

export function libellePassage(p: Passage): string {
  // Repere montre : distance lue sur la montre (l'arrivee reste la distance officielle).
  const montre = Math.abs(p.km - p.kmOfficiel) > EPS ? ' montre' : '';
  if (p.arrivee) return `Arrivée (${formaterKm(p.kmOfficiel)} km${montre ? ` ; ${formaterKm(p.km, 2)} km montre` : ''})`;
  if (p.miCourse) return `Mi-course (${formaterKm(p.km, 2)} km${montre})`;
  return `${formaterKm(p.km)} km${montre}`;
}
